import { Field, Input, InputOnChangeData, InputProps } from "@fluentui/react-components";
import React, { useEffect, useCallback, useMemo } from "react";

import { useDeviceDrawerStore } from "../../store/DeviceDrawerStore";

import BodyPresenceMotion from "./BodyPresenceMotion";
import BaseOptionCombobox from "../../../../components/BaseOptionCombobox";

import {
  CategoryStateProps,
  InputStateProps,
} from "../../hooks/useDeviceFormHook";

type BasicInfoProps = {
  styles: string;
  typeState: CategoryStateProps;
  labelState: InputStateProps<string>;
  serialNumberState: InputStateProps<string>;
  ownerState: CategoryStateProps;
  locationState: CategoryStateProps;
  getLabelPrefix: (optionId: number) => string;
};

export default function BasicInfo({
  styles,
  typeState,
  labelState,
  serialNumberState,
  ownerState,
  locationState,
  getLabelPrefix,
}: BasicInfoProps) {
  const { isForward, level } = useDeviceDrawerStore();
  
  useEffect(() => {
    if (typeState.value === 0 && labelState.value !== "") {
      labelState.setValue("");
    }
  }, [typeState.value, labelState]);

  const prefix = useMemo(
    () => getLabelPrefix(typeState.value),
    [typeState.value, getLabelPrefix]
  );

  const labelInputValue = useMemo(() => {
    if (!labelState.value) return "";
    if (labelState.value.startsWith(prefix)) {
      return labelState.value.slice(prefix.length);
    }
    return labelState.value;
  }, [labelState.value, prefix]);

  const handleInputChange: InputProps["onChange"] = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
      labelState.setValue(prefix + data.value);
    },
    [labelState, prefix]
  );

  return (
    <BodyPresenceMotion
      isForward={isForward}
      visible={level === 1}
      unmountOnExit={true}
    >
      <div className={styles}>
        <Field label={"Type"} required>
          <BaseOptionCombobox
            id={typeState.id}
            data={typeState.list?.options}
            defaultValue={typeState.value.toString()}
            disabled={false}
            onChange={typeState.setValue}
          />
        </Field>
        <Field label={"Label"} required>
          <Input
            contentBefore={prefix}
            id={labelState.id}
            type="text"
            maxLength={20}
            value={labelInputValue}
            disabled={typeState.value === 0}
            onChange={handleInputChange}
          />
        </Field>
        <Field label={"Serial Number"} required>
          <Input
            id={serialNumberState.id}
            type="text"
            maxLength={50}
            value={serialNumberState.value}
            onChange={(_, data) => serialNumberState.setValue(data.value)}
          />
        </Field>
        <Field label={"Owner"} required>
          <BaseOptionCombobox
            id={ownerState.id}
            data={ownerState.list?.options}
            defaultValue={ownerState.value.toString()}
            disabled={false}
            onChange={ownerState.setValue}
          />
        </Field>
        <Field label={"Location"} required>
          <BaseOptionCombobox
            id={locationState.id}
            data={locationState.list?.options}
            defaultValue={locationState.value?.toString() ?? ""}
            disabled={false}
            onChange={locationState.setValue}
          />
        </Field>
      </div>
    </BodyPresenceMotion>
  );
}
